<!-- this is the view for the Tutor Register form -->
<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from "vue";
import { useRouter } from "vue-router";
import TutorInfoAcademic from "@/components/TutorInfoAcademic.vue";
import TutorDocumentsAndTerms from "@/components/TutorDocumentsAndTerms.vue";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import {
  auth,
  db,
  storage,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  signInWithEmailAndPassword,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  storageRef,
  uploadBytes,
  getDownloadURL,
  onAuthStateChanged,
} from "@/firebase";
import { collection, query, where, getDocs, getDoc, deleteDoc } from "firebase/firestore";
import { useTutorRegistrationStorage, type TutorAgreementsData } from "@/composables/useRegistrationStorage";
import { logDebug, logInfo, logError } from '@/utils/secureLogger';

const router = useRouter();

// Initialize registration storage
const {
  loadTutorInfo,
  loadTutorDocuments,
  loadTutorAgreements,
  clearTutorRegistration,
} = useTutorRegistrationStorage();

// Current step in the registration process
const currentStep = ref(1);

// Loading state for registration
const isRegistering = ref(false);

// Store tutor data from step 1
const tutorData = ref<any>(null);

// Store documents and agreements from step 2
const documentsData = ref<Record<string, File | null>>({});
const agreementsData = ref<Partial<TutorAgreementsData>>({});

// Store complete registration data for final Firebase creation
const completeRegistrationData = ref<any>(null);

// SPA Scroll Management - Proper solution for Single Page Applications
const scrollToTop = async () => {
  // Method 1: Immediate scroll (for instant feedback)
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  
  // Method 2: Wait for Vue to finish rendering, then scroll again
  await nextTick();
  window.scrollTo({ top: 0, behavior: 'instant' });
  
  // Method 3: Final fallback after a small delay (handles any remaining async operations)
  setTimeout(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  }, 10);
};

// Watch for step changes and automatically scroll to top (SPA solution)
watch(currentStep, async (newStep, oldStep) => {
  if (newStep !== oldStep) {
    logDebug(`Tutor step changed from ${oldStep} to ${newStep} - scrolling to top`);
    await scrollToTop();
  }
});

// Load saved data on mount
onMounted(() => {
  const savedInfo = loadTutorInfo();
  const savedAgreements = loadTutorAgreements();
  
  if (savedInfo) {
    logDebug('Restored tutor info from sessionStorage');
    tutorData.value = savedInfo as any; // Will be populated with passwords by user
  }
  
  if (savedAgreements) {
    logDebug('Restored tutor agreements from sessionStorage');
    agreementsData.value = savedAgreements;
  }
  
  // Note: Documents (files) cannot be restored from sessionStorage
});

// Handle completion of step 1 (personal and academic info)
function handleInfoComplete(data: any) {
  logDebug("Received tutor info");
  tutorData.value = data;
  
  currentStep.value = 2;
  
  // Scroll will be handled automatically by the watcher
}

// Handle going back to previous step
function goToPreviousStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    
    // Scroll will be handled automatically by the watcher
  }
}

// Handle data changes in step 2 to preserve state
function handleDataChanged(data: any) {
  documentsData.value = data.documents;
  agreementsData.value = data.agreements;
}

// Handle completion of step 2 - store data and redirect to email verification
async function handleRegistrationComplete(data: any) {
  logInfo("TutorRegisterView: Step 2 completed - preparing for email verification");

  // Store all registration data for Firebase user creation later
  completeRegistrationData.value = data;

  // Set email for verification step from tutorData
  const email = data?.tutorData?.email || "";

  if (!email) {
    logError("TutorRegisterView: Missing email from tutorData");
    alert("Missing email from previous step. Please go back and re-enter your info.");
    currentStep.value = 1;
    return;
  }

  try {
    isRegistering.value = true; // Start loading
    logInfo("TutorRegisterView: All data collected, creating Firebase user...");

    // Validate critical data before proceeding
    const password = data?.tutorData?.password;
    if (!password || password.length < 6) {
      throw {
        code: 'auth/weak-password',
        message: 'Password must be at least 6 characters long'
      };
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw {
        code: 'auth/invalid-email',
        message: 'Please enter a valid email address'
      };
    }
    
    logInfo(`✅ Validation passed for email: ${email}`);

    // Check if there's an existing user (from previous registration attempt)
    const existingUser = auth.currentUser;
    let firebaseUid: string;
    
    if (existingUser && !existingUser.emailVerified && existingUser.email === email) {
      // Same email - reuse existing account instead of creating a new one
      logInfo(`♻️ Reusing existing account for: ${email}`);
      firebaseUid = existingUser.uid;
      
      // Update existing Firestore document with new data
      const userDocRef = doc(db, "users", firebaseUid);
      
      // Remove sensitive fields (password, confirmPassword) before storing
      const { password, confirmPassword, ...tutorDataWithoutPassword } = completeRegistrationData.value.tutorData;
      
      const userData = {
        ...tutorDataWithoutPassword,
        userType: "tutor",
        applicationStatus: "pending",
        profileStatus: "pending_approval",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      
      await setDoc(userDocRef, userData, { merge: true });
      
      // Upload documents
      const uploadedDocuments = await uploadDocuments(
        firebaseUid,
        completeRegistrationData.value.documents
      );
      
      // Update with uploaded documents and agreements
      await updateDoc(userDocRef, {
        uploadedDocuments,
        agreements: completeRegistrationData.value.agreements,
      });
      
      // Resend verification email with custom action code settings
      const actionCodeSettings = {
        url: `${window.location.origin}/email-verification?email=${encodeURIComponent(existingUser.email || '')}`,
        handleCodeInApp: true,
      };
      await sendEmailVerification(existingUser, actionCodeSettings);
      logInfo("✅ Resent verification email to existing account");
      
    } else if (existingUser && !existingUser.emailVerified && existingUser.email !== email) {
      // Email changed - clean up old account and create new one
      logInfo(`🔄 Email changed from ${existingUser.email} to ${email}. Cleaning up old account...`);
      
      const oldUid = existingUser.uid;
      const oldEmail = existingUser.email;
      
      logInfo(`📋 Old account details - UID: ${oldUid}, Email: ${oldEmail}`);
      
      // Delete old Firestore document
      try {
        const oldUserDocRef = doc(db, "users", oldUid);
        logInfo(`🔍 Checking for Firestore document: users/${oldUid}`);
        const oldUserDoc = await getDoc(oldUserDocRef);
        
        if (oldUserDoc.exists()) {
          logInfo(`📄 Found Firestore document for ${oldEmail}, deleting...`);
          await deleteDoc(oldUserDocRef);
          logInfo(`✅ Deleted old Firestore document for: ${oldEmail}`);
        } else {
          logInfo(`⚠️ No Firestore document found for UID: ${oldUid}`);
        }
      } catch (err) {
        logError("❌ Error deleting old Firestore doc:", err);
      }
      
      // Try to delete Firebase Auth user
      try {
        // Get the old password from the submitted data
        const oldPassword = completeRegistrationData.value?.tutorData?.password;
        
        if (oldPassword && oldEmail) {
          logInfo(`🔐 Attempting to reauthenticate and delete Firebase Auth user: ${oldEmail}`);
          
          // Reauthenticate the user with their credentials
          await signInWithEmailAndPassword(auth, oldEmail, oldPassword);
          logInfo(`✅ Reauthenticated successfully`);
          
          // Now delete the auth user
          const userToDelete = auth.currentUser;
          if (userToDelete) {
            await userToDelete.delete();
            logInfo(`✅ Deleted Firebase Auth account for: ${oldEmail}`);
          }
        } else {
          logInfo(`⚠️ Cannot delete auth user - missing password or email`);
        }
      } catch (err: any) {
        logError("❌ Error deleting Firebase Auth user:", err);
        // Continue anyway - we'll sign out and create new account
      }
      
      // Sign out if still authenticated
      try {
        if (auth.currentUser) {
          logInfo(`👋 Signing out user`);
          await signOut(auth);
          logInfo("✅ Signed out");
        }
      } catch (err) {
        logError("❌ Error signing out:", err);
      }
      
      // Create new Firebase user and store all data
      logInfo(`🆕 Creating new account for: ${email}`);
      firebaseUid = await createFirebaseUserAndStoreData();
      
    } else {
      // No existing user or first registration - create new account
      firebaseUid = await createFirebaseUserAndStoreData();
    }

    // Update Firestore with registration completion
    await updateDoc(doc(db, "users", firebaseUid), {
      isEmailVerified: false, // Will be true after user clicks email link
      updatedAt: serverTimestamp(),
      registrationCompleted: true,
    });

    // Set up listener to update isVerified when user verifies their email
    setupEmailVerificationListener(firebaseUid);

    logInfo("Registration completed successfully!");
    logInfo("Verification email sent! User must click the link to verify their email.");

    // DON'T clear sessionStorage yet - keep data in case user needs to change email
    // It will be cleared when they successfully verify their email
    
    // Redirect to email verification page
    router.push({
      path: "/email-verification",
      query: {
        email: email,
        from: "tutor-register",
      },
    });
  } catch (error: any) {
    isRegistering.value = false; // Stop loading on error
    logError("Error completing registration:", error);
    logError(`Error details - code: ${error?.code}, message: ${error?.message}`);

    // Handle specific Firebase errors
    let errorMsg = "Registration failed. Please try again.";
    let errorDetails = "";

    if (error.code === "auth/email-already-in-use") {
      errorMsg =
        "An account with this email already exists. Please use a different email or try logging in.";
    } else if (error.code === "auth/weak-password") {
      errorMsg = "Password is too weak. Please use at least 6 characters with uppercase, lowercase, and numbers.";
    } else if (error.code === "auth/invalid-email") {
      errorMsg = "Please enter a valid email address.";
    } else if (error.code === "auth/operation-not-allowed") {
      errorMsg = "Email/password sign-in is not enabled. Please contact support.";
    } else if (error.code === "auth/network-request-failed") {
      errorMsg = "Network error. Please check your internet connection and try again.";
    } else if (error.message?.includes("upload")) {
      errorMsg = error.message;
    } else if (error.message) {
      errorDetails = `\n\nDetails: ${error.message}`;
    }

    alert(errorMsg + errorDetails);
    
    // Log to console for debugging
    console.error("Full registration error:", {
      code: error?.code,
      message: error?.message,
      stack: error?.stack,
      error
    });
  }
}

// Upload documents to Firebase Storage
async function uploadDocuments(
  userId: string,
  documents: Record<string, File>
): Promise<Record<string, any>> {
  const uploadedDocs: Record<string, any> = {};

  for (const [docType, file] of Object.entries(documents)) {
    if (file && file instanceof File) {
      try {
        // Create a unique filename
        const timestamp = Date.now();
        const fileExtension = file.name.split(".").pop();
        const fileName = `${docType}_${timestamp}.${fileExtension}`;

        // Create storage reference
        const fileRef = storageRef(storage, `tutorDocuments/${userId}/${fileName}`);

        // Upload file
        logDebug(`Uploading ${docType}`);
        const snapshot = await uploadBytes(fileRef, file);

        // Get download URL
        const downloadURL = await getDownloadURL(snapshot.ref);

        uploadedDocs[docType] = {
          name: file.name,
          url: downloadURL,
          type: file.type,
          size: file.size,
        };

        logInfo(`Successfully uploaded ${docType}`);
      } catch (error) {
        logError(`Failed to upload ${docType}:`, error);
        throw new Error(`Failed to upload ${docType}. Please try again.`);
      }
    }
  }

  return uploadedDocs;
}

// Create Firebase user and store all data (similar to student registration)
async function createFirebaseUserAndStoreData() {
  if (!completeRegistrationData.value || !completeRegistrationData.value.tutorData) {
    throw new Error("Tutor data not available");
  }

  const regData = completeRegistrationData.value;

  try {
    logInfo("Creating Firebase user");

    // 0. Check if email already exists in Firestore (case-insensitive)
    const emailToCheck = regData.tutorData.email.toLowerCase().trim();
    logInfo(`Checking if email already exists: ${emailToCheck}`);
    
    const usersRef = collection(db, "users");
    const emailQuery = query(usersRef, where("email", "==", emailToCheck));
    const querySnapshot = await getDocs(emailQuery);
    
    logInfo(`Firestore query returned ${querySnapshot.size} results`);
    
    if (!querySnapshot.empty) {
      logError(`Email already exists in Firestore: ${emailToCheck}`);
      querySnapshot.forEach(doc => {
        logInfo(`Found user: ${doc.id} with email: ${doc.data().email}`);
      });
      throw {
        code: "auth/email-already-in-use",
        message: "Email already registered"
      };
    }
    logInfo("Email is available in Firestore, proceeding with user creation");

    // 1. Create user with email and password
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      regData.tutorData.email,
      regData.tutorData.password
    );
    const user = userCredential.user;
    logInfo("Firebase user created");

    // 2. Send email verification with custom action code settings
    const actionCodeSettings = {
      url: `${window.location.origin}/email-verification?email=${encodeURIComponent(user.email || '')}`,
      handleCodeInApp: true,
    };
    await sendEmailVerification(user, actionCodeSettings);
    logInfo("Email verification sent");

    // 3. Upload documents to Firebase Storage
    logInfo("Uploading documents to Firebase Storage...");
    const uploadedDocuments = await uploadDocuments(user.uid, regData.documents);
    logInfo("All documents uploaded successfully");

    // 4. Store complete user data in Firestore
    const userData = {
      // Firebase Auth Info
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,

      // Personal Information
      firstName: regData.tutorData.firstName,
      middleName: regData.tutorData.middleName,
      lastName: regData.tutorData.lastName,
      suffix: regData.tutorData.suffix,
      dateOfBirth: regData.tutorData.dateOfBirth,

      // Academic Information
      educationLevel: regData.tutorData.educationLevel,
      fieldOfStudy: regData.tutorData.fieldOfStudy,
      institution: regData.tutorData.institution,
      graduationYear: parseInt(regData.tutorData.graduationYear),
      experienceYears: parseInt(regData.tutorData.experienceYears),
      subjects: regData.tutorData.subjects,

      // Professional Information (populated from registration data)
      education: [
        {
          degree: regData.tutorData.educationLevel,
          school: regData.tutorData.institution,
          fieldOfStudy: regData.tutorData.fieldOfStudy,
          details: `Graduated ${regData.tutorData.graduationYear}`,
        },
      ],
      certifications: [],
      languages: [],

      // Contact Information (initialized as empty for later editing)
      phoneNumber: "",
      address: "",
      linkedInProfile: "",
      personalWebsite: "",
      bio: "",

      // User Type
      userType: "tutor",
      role: "Tutor",

      // Documents (Firebase Storage URLs)
      documents: uploadedDocuments,

      // Agreements
      agreements: {
        studentSafety: regData.agreements.studentSafety || false,
        liabilityTerms: regData.agreements.liabilityTerms || false,
        conductStandards: regData.agreements.conductStandards || false,
        termsAndConditions: regData.agreements.termsAndConditions || false,
        legalNotice: regData.agreements.legalNotice || false,
      },

      // Status flags
      status: "Active", // Account status
      profileStatus: "pending_approval", // Admin must approve
      applicationStatus: "pending", // Changed from "submitted" to "pending" to match our status system
      isEmailVerified: false, // Will be true after user clicks email link
      isVerified: false, // Will be true after user clicks email verification link - controls login access
      isProfileComplete: true,
      isDocumentsUploaded: Object.keys(uploadedDocuments).length > 0,
      registrationCompleted: false, // Will be updated after email verification

      // Timestamps
      applicationDate: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),

      // Additional metadata
      registrationStep: "email_verification_pending",
      registrationVersion: "1.0",
    };

    // Debug: Check for any undefined values before storing
    logDebug("Agreements and documents prepared for storage");

    // SECURITY: Ensure no password fields are accidentally included
    delete (userData as any).password;
    delete (userData as any).confirmPassword;

    logInfo("Storing complete user data in Firestore");
    await setDoc(doc(db, "users", user.uid), userData);
    logInfo("User data stored successfully in Firestore");

    return user.uid;
  } catch (error: any) {
    logError("Error creating Firebase user:", error);
    logError(`Error code: ${error?.code}`);
    logError(`Error message: ${error?.message}`);
    
    // If user was created but something failed, try to delete them
    if (auth.currentUser && !error?.code?.includes('email-already-in-use')) {
      try {
        logInfo("Attempting to cleanup partially created user");
        await auth.currentUser.delete();
        logInfo("Cleanup successful");
      } catch (cleanupError) {
        logError("Cleanup failed:", cleanupError);
      }
    }
    
    throw error;
  }
}

// Manual function to check and update verification status
async function checkAndUpdateVerificationStatus(userId: string) {
  try {
    logDebug("Manually checking verification status for user");

    if (auth.currentUser && auth.currentUser.uid === userId) {
      // Reload user to get the latest emailVerified status from Firebase Auth
      await auth.currentUser.reload();

      if (auth.currentUser.emailVerified) {
        logInfo("Email verified detected! Updating isVerified status in Firestore...");

        // Update Firestore with verified status
        await updateDoc(doc(db, "users", userId), {
          isEmailVerified: true,
          isVerified: true, // This controls login access
          emailVerifiedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        logInfo("User verification status updated successfully! isVerified = true");
        return true;
      } else {
        logDebug("Email not yet verified");
        return false;
      }
    } else {
      logDebug("User ID mismatch or no current user");
    }

    return false;
  } catch (error: any) {
    // Handle token expiration errors silently (happens when old user was signed out)
    if (error?.code === 'auth/user-token-expired' || error?.code === 'auth/requires-recent-login') {
      logDebug("Auth token expired - user likely signed out or switched accounts");
      return false;
    }
    logError("Error checking verification status:", error);
    return false;
  }
}

// Set up listener to update isVerified when user verifies their email
function setupEmailVerificationListener(userId: string) {
  logDebug("Setting up email verification listener for user");

  // Set up a more robust checking mechanism
  let checkInterval: number;

  // Listen for auth state changes to detect when user verifies email
  const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (user && user.uid === userId) {
      logDebug("Auth state changed - checking verification status...");

      // Check verification status
      const isVerified = await checkAndUpdateVerificationStatus(userId);

      if (isVerified) {
        logInfo("Verification complete! Cleaning up listeners...");
        // Clear interval and unsubscribe since verification is complete
        if (checkInterval) clearInterval(checkInterval);
        unsubscribe();
        return;
      }

      // Set up periodic checking every 3 seconds to catch verification
      if (!checkInterval) {
        logDebug("Setting up periodic verification check...");
        checkInterval = Number(
          setInterval(async () => {
            const verified = await checkAndUpdateVerificationStatus(userId);
            if (verified) {
              logInfo("Verification detected via periodic check!");
              clearInterval(checkInterval);
              unsubscribe();
            }
          }, 1000)
        ); // Check every 3 seconds
      }
    }
  });

  // Also do an immediate check in case user is already verified
  setTimeout(() => {
    checkAndUpdateVerificationStatus(userId);
  }, 1000);

  // Store unsubscribe function for cleanup if needed
  return () => {
    if (checkInterval) clearInterval(checkInterval);
    unsubscribe();
  };
}

function goToLogin() {
  router.push("/login");
}

function goToHome() {
  router.push("/");
}
</script>

<template>
  <Header />

  <!-- Loading Overlay -->
  <div v-if="isRegistering" class="loading-overlay">
    <div class="loading-container">
      <div class="arrow-loader">
        <img src="/elevated_load.png" alt="" class="arrow-base" />
        <img src="/elevated_arrow.png" alt="" class="arrow-wipe" />
      </div>
      <p class="loading-text">Creating your account...</p>
    </div>
  </div>

  <div class="registration-view">
    <!-- Step 1: Personal and Academic Information -->
    <TutorInfoAcademic
      v-if="currentStep === 1"
      :initial-data="tutorData"
      @tutor-info-complete="handleInfoComplete"
    />

    <!-- Step 2: Documents and Terms -->
    <TutorDocumentsAndTerms
      v-if="currentStep === 2"
      :tutor-data="tutorData"
      :initial-documents="documentsData"
      :initial-agreements="agreementsData"
      @registration-complete="handleRegistrationComplete"
      @go-back="goToPreviousStep"
      @data-changed="handleDataChanged"
    />

    <!-- Step 3: Final Success/Confirmation -->
    <div v-if="currentStep === 3" class="success-container">
      <!-- Progress Header (success state) -->
      <div class="progress-header">
        <div class="header-content">
          <h1 class="main-title">Registration Complete!</h1>
          <p class="subtitle">Thank you for applying to become a tutor</p>

          <!-- Step Progress (2 steps - both completed) -->
          <div class="step-progress">
            <div class="step completed">
              <span class="step-number">1</span>
              <span class="step-label">Personal & Academic Info</span>
            </div>
            <div class="step-line"></div>
            <div class="step completed">
              <span class="step-number">2</span>
              <span class="step-label">Documents & Terms</span>
            </div>
          </div>
        </div>
      </div>

      <div class="content-wrapper">
        <div class="success-card">
          <div class="success-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
            </svg>
          </div>
          <h1 class="success-title">Registration Submitted Successfully!</h1>
          <p class="success-message">
            Thank you for applying to become a tutor on ElevatED. Your application has been
            submitted and you will be redirected to verify your email address.
          </p>

          <div class="next-steps">
            <h3>What happens next?</h3>
            <div class="step-list">
              <div class="step-item">
                <div class="step-number">1</div>
                <div class="step-content">
                  <h4>Email Verification</h4>
                  <p>
                    You will be redirected to verify your email address. Check your email and click
                    the verification link we sent to
                    {{ tutorData?.email }}
                  </p>
                </div>
              </div>
              <div class="step-item">
                <div class="step-number">2</div>
                <div class="step-content">
                  <h4>Application Review</h4>
                  <p>
                    Our team will review your documents and qualifications (typically 2-5 business
                    days)
                  </p>
                </div>
              </div>
              <div class="step-item">
                <div class="step-number">3</div>
                <div class="step-content">
                  <h4>Account Activation</h4>
                  <p>
                    Once approved, you'll receive an email with instructions to complete your
                    profile
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="actions">
            <button @click="goToLogin" class="login-btn">Go to Login</button>
            <button @click="goToHome" class="home-btn">Back to Home</button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Footer />
</template>

<style scoped>
.registration-view {
  width: 100%;
  min-height: 100vh;
  background: url("/tutor-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.arrow-loader {
  position: relative;
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.arrow-base {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: contain;
}



.arrow-wipe {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: contain;
  clip-path: inset(100% 0 0 0);
  animation: wipeUp 3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  z-index: 2;
}

@keyframes wipeUp {
  0% {
    clip-path: inset(100% 0 0 0);
    opacity: 0;
  }
  5% {
    opacity: 1;
  }
  45% {
    clip-path: inset(0% 0 0 0);
    opacity: 1;
  }
  50% {
    clip-path: inset(0% 0 0 0);
    opacity: 1;
  }
  85% {
    clip-path: inset(0 0 100% 0);
    opacity: 1;
  }
  95% {
    opacity: 0;
  }
  100% {
    clip-path: inset(0 0 100% 0);
    opacity: 0;
  }
}

.loading-text {
  font-size: 18px;
  font-weight: 500;
  color: #556fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  text-align: center;
}

.success-container {
  min-height: 100vh;
  background: #f5f5f5;
  position: relative;
}

.progress-header {
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%);
  padding: 40px 20px 30px;
  padding-top: 120px; /* Add top padding to account for header height */
  text-align: center;
  position: relative;
}

.header-content {
  max-width: 600px;
  margin: 0 auto;
}

.main-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.subtitle {
  font-size: 1.1rem;
  color: #6b7280;
  margin: 0 0 40px 0;
  font-style: italic;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.step-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.step-number {
  width: 40px;
  height: 40px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: #3b82f6;
  border-color: #3b82f6;
  color: white;
}

.step.completed .step-number {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.step-label {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
  max-width: 80px;
  line-height: 1.2;
}

.step.active .step-label {
  color: #1e40af;
  font-weight: 600;
}

.step.completed .step-label {
  color: #059669;
  font-weight: 600;
}

.step-line {
  width: 40px;
  height: 2px;
  background: #d1d5db;
  margin: 0 4px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  background: #f5f5f5;
}

.progress-section {
  position: absolute;
  top: 100px; /* Adjust based on header height */
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 800px;
  padding: 0 20px;
  box-sizing: border-box;
}

.progress-container {
  display: flex;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  background: #e0e0e0;
  border-radius: 10px;
  padding: 5px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-step {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #94a3b8;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  z-index: 1;
}

.progress-step.completed {
  background: #10b981;
}

.progress-step.active {
  background: #1e40af;
}

.progress-line {
  flex-grow: 1;
  height: 2px;
  background: #e0e0e0;
  position: relative;
}

.progress-line.completed::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 100%;
  background: #10b981;
  transform: translateY(-50%);
  z-index: -1;
}

.success-card {
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 600px;
  text-align: center;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: #10b981;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  color: white;
}

.success-icon svg {
  width: 40px;
  height: 40px;
}

.success-title {
  color: #1e40af;
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 16px;
}

.success-message {
  color: #3b82f6;
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 40px;
}

.next-steps {
  text-align: left;
  margin-bottom: 40px;
}

.next-steps h3 {
  color: #1e40af;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
}

.step-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  background: #f9fafb;
  border-radius: 12px;
  border-left: 4px solid #1e40af;
}

.step-number {
  width: 32px;
  height: 32px;
  background: #1e40af;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
}

.step-content h4 {
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}

.step-content p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}

.actions {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.login-btn,
.home-btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.login-btn {
  background: #1e40af;
  color: white;
}

.login-btn:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.home-btn {
  background: #6b7280;
  color: white;
}

.home-btn:hover {
  background: #4b5563;
  transform: translateY(-1px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .success-container {
    padding-top: 150px;
  }

  .progress-section {
    top: 150px;
  }

  .progress-container {
    max-width: 400px;
  }

  .progress-step {
    width: 36px;
    height: 36px;
    font-size: 14px;
  }

  .success-card {
    padding: 24px;
    margin: 10px;
  }

  .success-title {
    font-size: 28px;
  }

  .success-message {
    font-size: 16px;
  }

  .actions {
    flex-direction: column;
  }

  .login-btn,
  .home-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .success-container {
    padding: 20px 10px;
    padding-top: 120px;
  }

  .progress-section {
    top: 120px;
    padding: 0 10px;
  }

  .progress-container {
    max-width: 300px;
  }

  .progress-step {
    width: 32px;
    height: 32px;
    font-size: 12px;
  }

  .success-card {
    padding: 20px;
    margin: 5px;
  }

  .success-title {
    font-size: 24px;
  }

  .success-message {
    font-size: 14px;
  }

  .success-icon {
    width: 60px;
    height: 60px;
  }

  .success-icon svg {
    width: 30px;
    height: 30px;
  }

  .step-item {
    padding: 16px;
  }

  .step-number {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
}
</style>
